"use client";

import {
  useCallback,
  useEffect,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import type { Photo } from "@/lib/immich/types";
import {
  BuyMeACoffeePopover,
  type BuyMeACoffeeFreeBody,
  type BuyMeACoffeeLabels,
} from "./BuyMeACoffeePopover";
import { DownloadButton } from "./DownloadButton";
import { LiveLoopButton } from "./LiveLoopButton";
import { LivePhotoVideo } from "./LivePhotoVideo";
import { PhotoImage } from "./PhotoImage";
import { PhotoNavButton } from "./PhotoNavButton";
import { useAdjacentPreload } from "./useAdjacentPreload";
import { useFailedImages } from "./useFailedImages";
import { usePhotoNavigation } from "./usePhotoNavigation";
import css from "./PhotoModalViewer.module.css";

export interface PhotoModalViewerLabels {
  readonly previous: string;
  readonly next: string;
  readonly imageFailed: string;
  readonly liveLoop: string;
  readonly liveStopLoop: string;
}

export interface PhotoModalViewerDownloadConfig {
  readonly label: string;
  readonly bmcHref: string;
  readonly bmcLabels: BuyMeACoffeeLabels;
  /** Present for "free" codes — swaps the BMC body for the email-help copy. */
  readonly bmcFreeBody?: BuyMeACoffeeFreeBody;
}

export interface PhotoModalViewerProps {
  readonly photos: readonly Photo[];
  readonly index: number;
  readonly onIndexChange: (next: number) => void;
  readonly labels: PhotoModalViewerLabels;
  /** When present, renders the download button + BMC notice in the toolbar. */
  readonly download?: PhotoModalViewerDownloadConfig;
  /** Optional extra toolbar slot (e.g. share button in US5). */
  readonly toolbar?: ReactNode;
}

export function PhotoModalViewer({
  photos,
  index,
  onIndexChange,
  labels,
  download,
  toolbar,
}: PhotoModalViewerProps) {
  const current = photos[index];
  const [isBmcOpen, setBmcOpen] = useState(false);
  const openBmc = useCallback(() => setBmcOpen(true), []);

  // Loop state is local to the modal session; navigating to a different
  // photo resets it (a fresh photo defaults back to play-once).
  const [isLooping, setLooping] = useState(false);
  useEffect(() => {
    setLooping(false);
  }, [current?.id]);
  const toggleLoop = useCallback(() => setLooping((v) => !v), []);

  const {
    canPrev,
    canNext,
    goPrev,
    goNext,
    handleKeyDown,
    handleTouchStart,
    handleTouchEnd,
  } = usePhotoNavigation(index, photos.length, onIndexChange);
  const { hasFailed, markFailed } = useFailedImages();
  useAdjacentPreload(photos, index);

  // The DS Dialog moves focus to the close button on open, so the viewer div
  // rarely receives keydown events. Listen on window while mounted so arrow
  // keys navigate regardless of focus location.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        goPrev();
      } else if (event.key === "ArrowRight") {
        event.preventDefault();
        goNext();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [goPrev, goNext]);

  if (!current) return null;

  // Drive the rendered photo size from its intrinsic ratio. The image's CSS
  // uses `inline-size: min(viewport-cap, height-cap × var(--photo-aspect))`
  // so it scales as large as the viewport allows while preserving the ratio,
  // and the dialog (display: block) hugs the result — making the dialog's
  // own aspect ratio match the photo's.
  const aspectStyle: CSSProperties = {
    "--photo-aspect": `${current.width / current.height}`,
  } as CSSProperties;

  return (
    <div
      className={css.viewer}
      role="group"
      aria-label={current.alt}
      tabIndex={-1}
      onKeyDown={handleKeyDown}
      style={aspectStyle}
    >
      <div
        className={css.imageFrame}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <PhotoImage
          photo={current}
          failed={hasFailed(current.id)}
          onError={() => markFailed(current.id)}
          errorLabel={labels.imageFailed}
        />

        {current.livePhotoUrl ? (
          <LivePhotoVideo
            src={current.livePhotoUrl}
            photoId={current.id}
            loop={isLooping}
          />
        ) : null}

        <PhotoNavButton
          direction="prev"
          onClick={goPrev}
          disabled={!canPrev}
          label={labels.previous}
        />
        <PhotoNavButton
          direction="next"
          onClick={goNext}
          disabled={!canNext}
          label={labels.next}
        />

        {(download || toolbar || current.livePhotoUrl) ? (
          <div className={css.toolbar}>
            {current.livePhotoUrl ? (
              <LiveLoopButton
                isLooping={isLooping}
                onToggle={toggleLoop}
                labelLoop={labels.liveLoop}
                labelStopLoop={labels.liveStopLoop}
              />
            ) : null}
            {download ? (
              <DownloadButton
                href={current.downloadUrl}
                label={download.label}
                onDownloadStart={openBmc}
              />
            ) : null}
            {toolbar}
          </div>
        ) : null}

        {download && isBmcOpen ? (
          <div className={css.bmcOverlay}>
            <BuyMeACoffeePopover
              href={download.bmcHref}
              labels={download.bmcLabels}
              freeBody={download.bmcFreeBody}
            />
          </div>
        ) : null}

        {current.exif.description && !isBmcOpen && !hasFailed(current.id) ? (
          <div className={css.caption}>
            <p className={css.captionText}>{current.exif.description}</p>
          </div>
        ) : null}
      </div>
    </div>
  );
}
